"use client";

import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { resetClientIdentityCaches } from "@/lib/auth/client-session";
import { analytics } from "@/lib/analytics/analytics";
import { AnalyticsEvents } from "@/lib/analytics/events";
import { PASSWORD_MIN_LENGTH } from "@/lib/auth/password";
import { avatarOnboardingPath } from "@/lib/auth/onboarding-routes";

function humanizeOAuthError(raw: string): string {
  if (raw.includes("PKCE code verifier not found")) {
    return "Sign-in expired or was interrupted. Please try again.";
  }
  return raw;
}

export function useAuthForm(initialSignUp = false) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isSignUp, setIsSignUp] = useState(initialSignUp);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(null);

  useEffect(() => {
    setIsSignUp(initialSignUp);
  }, [initialSignUp]);

  useEffect(() => {
    const oauthError = new URLSearchParams(window.location.search)
      .get("error")
      ?.trim();
    if (!oauthError) return;
    setError(humanizeOAuthError(decodeURIComponent(oauthError)));
  }, []);

  const submitCredentials = useCallback(
    async (e?: React.FormEvent, avatarOverride?: string | null) => {
      e?.preventDefault();
      const minLen = isSignUp ? PASSWORD_MIN_LENGTH : 1;
      if (!username.trim() || password.length < minLen) return;

      setIsLoading(true);
      setError("");

      try {
        const body: Record<string, unknown> = {
          username,
          password,
          pin: password,
          isSignUp,
        };
        const avatar = avatarOverride ?? selectedAvatar;
        if (isSignUp && avatar) body.avatar_url = avatar;
        if (isSignUp && email.trim()) body.email = email.trim();

        const res = await fetch("/api/auth", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        const raw = await res.text();
        let payload: {
          success?: boolean;
          error?: string;
          data?: { redirectTo?: string };
        };
        try {
          payload = raw ? (JSON.parse(raw) as typeof payload) : {};
        } catch {
          throw new Error(
            res.ok
              ? "Authentication failed"
              : `Authentication failed (${res.status})`,
          );
        }

        if (!res.ok || !payload.success) {
          throw new Error(payload.error || "Authentication failed");
        }

        if (isSignUp) {
          analytics.capture(AnalyticsEvents.accountCreated, {
            signup_method: "username-password",
          });
        } else {
          analytics.capture(AnalyticsEvents.loginSuccess, {
            signup_method: "username-password",
          });
        }

        resetClientIdentityCaches(queryClient);

        const redirectTo =
          typeof payload.data?.redirectTo === "string" &&
          payload.data.redirectTo.startsWith("/")
            ? payload.data.redirectTo
            : isSignUp
              ? avatarOnboardingPath("/home")
              : "/home";
        router.replace(redirectTo);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Connection error. Try again.";
        setError(message);
        setIsLoading(false);
        if (isSignUp) setSelectedAvatar(null);
      }
    },
    [username, password, email, isSignUp, selectedAvatar, router, queryClient],
  );

  return {
    username,
    setUsername,
    email,
    setEmail,
    password,
    setPassword,
    rememberMe,
    setRememberMe,
    isSignUp,
    setIsSignUp,
    error,
    setError,
    isLoading,
    setIsLoading,
    selectedAvatar,
    setSelectedAvatar,
    submitCredentials,
  };
}
